import React from 'react'
import { Field, FormikProps } from 'formik'
import { RichTextInput } from '../../../../../../../components/inputs/RichTextInput/RichTextInput'
import { FormSectionTwoColumns } from '../../../../../../../components/forms/FormSectionTwoColumns'
import { DatePickerInput } from '../../../../../../../components/inputs/DatePickerInput'
import { CheckboxInput } from '../../../../../../../components/inputs/CheckboxInput'
import { AccompanyingReportInputsPreview } from '../../../../../../../components/AccompanyingReportInputsPreview'
import { InfoPopover } from '../../../../../../../components/InfoPopover'

interface Props {
  formik: FormikProps<any>
}

export const TechnicalFormB1Mv2: React.FC<Props> = ({ formik }) => {
  return (
    <FormSectionTwoColumns
      title="m) základní předpoklady výstavby"
      subtitle="časové údaje o realizaci stavby, členění na etapy, věcné a časové vazby stavby, podmiňující, vyvolané a související investice"
      sectionKey="b1m"
    >
      <div className="sm:col-span-6 mt-5 flex flex-col gap-3">
        <AccompanyingReportInputsPreview>
          <div className="flex flex-col gap-3">
            <div className="">
              <Field
                label="Termín zahájení"
                name="readonly.a4.plannedStartDate"
                component={DatePickerInput}
                disabled={true}
              />
            </div>
            <div className="">
              <Field
                label="Termín dokončení"
                name="readonly.a4.plannedEndDate"
                component={DatePickerInput}
                disabled={true}
              />
            </div>
          </div>
        </AccompanyingReportInputsPreview>
        <div>
          <Field
            name={`b1m.divisionToStages.enabled`}
            component={CheckboxInput}
            label="Členění na etapy"
          />
          {formik.values.b1m.divisionToStages.enabled && (
            <div className="pl-7">
              <Field
                name={`b1m.divisionToStages.details`}
                component={RichTextInput}
                aiHint="Členění na etapy"
              />
            </div>
          )}
        </div>
        <div>
          <Field
            component={CheckboxInput}
            name="b1m.objectAndTimeConnections"
            label="Věcné a časové vazby"
          />
          {formik.values?.b1m?.objectAndTimeConnections && (
            <div className="pl-7">
              <Field
                component={RichTextInput}
                name="b1m.objectAndTimeConnectionsDetails"
                aiHint="Věcné a časové vazby"
              />
            </div>
          )}
        </div>
        <div>
          <div className="flex">
            <Field
              component={CheckboxInput}
              name="b1m.conditionalInvestments"
              label="Podmiňující investice"
            />
            <InfoPopover
              content={
                <div className="text-left">
                  <ol className="list-decimal pl-5">
                    <li className="font-bold">
                      Podmiňující investice
                      <div className="font-normal mt-3">
                        <b>Definice:</b> Jedná se o investice, které je nezbytné realizovat před
                        zahájením hlavní stavby, aby byla vůbec možná její realizace. Tyto investice
                        připravují území, zajišťují přístup a infrastrukturu, kterou stavba vyžaduje
                        k zahájení nebo provedení prací.
                      </div>
                      <div className="font-normal mt-3">
                        <b>Účel:</b>
                        <ul className="pl-10 list-disc">
                          <li>Připravit pozemek a území pro hlavní stavbu.</li>
                          <li>
                            Zajistit přístup, inženýrské sítě a další nezbytné podmínky pro
                            výstavbu.
                          </li>
                        </ul>
                      </div>
                      <div className="font-normal my-3">
                        <b>Příklady:</b>
                        <ul className="pl-10 list-disc">
                          <li>Zřízení přípojek na inženýrské sítě (voda, elektřina, plyn).</li>
                          <li>Přeložky stávajících sítí kolidujících s plánovanou stavbou.</li>
                          <li>Terénní úpravy, odstranění překážek, odvodnění pozemku.</li>
                          <li>Vybudování přístupové cesty pro stavební techniku.</li>
                        </ul>
                      </div>
                    </li>

                    <li className="font-bold">
                      Vyvolané investice
                      <div className="font-normal mt-3">
                        <b>Definice:</b> Investice, které jsou nutné v důsledku realizace hlavní
                        stavby nebo jejího provozu. Tyto investice řeší vlivy a dopady stavby na
                        okolní prostředí, infrastrukturu nebo komunitu a zajišťují jejich zvládnutí
                        nebo kompenzaci.
                      </div>
                      <div className="font-normal mt-3">
                        <b>Účel:</b>
                        <ul className="pl-10 list-disc">
                          <li>Minimalizovat dopady stavby na okolí.</li>
                          <li>
                            Přizpůsobit nebo rozšířit infrastrukturu zasaženou zvýšenou zátěží.
                          </li>
                        </ul>
                      </div>
                      <div className="font-normal my-3">
                        <b>Příklady:</b>
                        <ul className="pl-10 list-disc">
                          <li>
                            Posílení kapacity čistírny odpadních vod kvůli nové bytové zástavbě.
                          </li>
                          <li>
                            Rozšíření silnice nebo křižovatky z důvodu zvýšení dopravní zátěže.
                          </li>
                          <li>Výstavba protihlukové stěny u silnice v blízkosti nového objektu.</li>
                          <li>Přeložka elektrického vedení zasaženého stavbou.</li>
                        </ul>
                      </div>
                    </li>

                    <li className="font-bold">
                      Související investice
                      <div className="font-normal mt-3">
                        <b>Definice:</b> Jedná se o doplňkové investice, které nejsou přímo nutné
                        pro realizaci nebo provoz stavby, ale zvyšují její užitnou hodnotu, estetiku
                        nebo komfort pro uživatele. Tyto investice rozšiřují doprovodnou
                        infrastrukturu a zlepšují integraci stavby do prostředí.
                      </div>
                      <div className="font-normal mt-3">
                        <b>Účel:</b>
                        <ul className="pl-10 list-disc">
                          <li>Doplňovat nebo podporovat hlavní stavbu a její funkčnost.</li>
                          <li>Zvyšovat estetickou a uživatelskou hodnotu stavby a okolí.</li>
                        </ul>
                      </div>
                      <div className="font-normal my-3">
                        <b>Příklady:</b>
                        <ul className="pl-10 list-disc">
                          <li>Výsadba zeleně a úpravy veřejného prostoru v okolí stavby.</li>
                          <li>
                            Zřízení laviček, dětského hřiště nebo cyklostezky u nové zástavby.
                          </li>
                          <li>
                            Instalace dobíjecích stanic pro elektromobily na přilehlém parkovišti.
                          </li>
                          <li>Úprava fasády nebo okolní krajiny pro zlepšení estetického dojmu.</li>
                        </ul>
                      </div>
                    </li>
                  </ol>

                  <p className="mt-10 my-3 font-bold">Klíčové rozdíly</p>
                  <div className="grid grid-cols-4 font-bold text-sm mb-3 gap-2 items-center">
                    <div>Typ investice</div>
                    <div>Vazba na stavbu</div>
                    <div>Nezbytnost realizace</div>
                    <div>Typ investice</div>
                  </div>
                  <div className="grid grid-rows-3 text-sm gap-2">
                    <div className="grid grid-cols-4 gap-2">
                      <div className="font-bold">Podmiňující</div>
                      <div>Předchází výstavbě, umožňuje její zahájení</div>
                      <div>Nutná pro realizaci stavby</div>
                      <div>
                        Připojení na vodovod, úprava terénu, vybudování přístupové komunikace.
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-2">
                      <div className="font-bold">Vyvolaná</div>
                      <div>Vyplývá z realizace a provozu stavby</div>
                      <div>Nutná kvůli dopadům stavby</div>
                      <div>
                        Rozšíření kanalizace, posílení elektrické sítě, výstavba protihlukové stěny.
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-2">
                      <div className="font-bold">Související</div>
                      <div>Doplňuje stavbu, zvyšuje její hodnotu</div>
                      <div>Nenutná, ale přínosná</div>
                      <div>Výsadba stromů, instalace laviček, zřízení dětského hřiště.</div>
                    </div>
                  </div>
                </div>
              }
            />
          </div>
          {formik.values.b1m?.conditionalInvestments && (
            <div className="pl-7">
              <Field
                component={RichTextInput}
                name="b1m.conditionalInvestmentsDetails"
                aiHint="Podmiňující investice - popis"
              />
            </div>
          )}
        </div>
        <div>
          <Field
            component={CheckboxInput}
            name="b1m.triggeredInvestments"
            label="Vyvolané investice"
          />
          {formik.values.b1m?.triggeredInvestments && (
            <div className="pl-7">
              <Field
                component={RichTextInput}
                name="b1m.triggeredInvestmentsDetails"
                aiHint="Vyvolané investice - popis"
              />
            </div>
          )}
        </div>
        <div>
          <Field
            component={CheckboxInput}
            name="b1m.relatedInvestments"
            label="Související investice"
          />
          {formik.values.b1m?.relatedInvestments && (
            <div className="pl-7">
              <Field
                component={RichTextInput}
                name="b1m.relatedInvestmentsDetails"
                aiHint="Související investice - popis"
              />
            </div>
          )}
        </div>
      </div>
    </FormSectionTwoColumns>
  )
}
